import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  MapPin,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import api from '../../services/api';
import { formatDate } from '../../utils/helpers';

const SubdivisionManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedSubdivision, setSelectedSubdivision] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    isActive: true,
    headUserId: ''
  });

  // Fetch all subdivisions (including inactive)
  const { data: subdivisionsData, isLoading } = useQuery(
    ['subdivisions-all', searchTerm],
    async () => {
      const response = await api.get('/subdivisions/all');
      return response.data;
    }
  );

  // Fetch eligible heads (ADC/DC users)
  const { data: eligibleHeadsData } = useQuery(
    ['eligible-heads'],
    async () => {
      const response = await api.get('/subdivisions/eligible-heads');
      return response.data;
    }
  );

  // Filter subdivisions by search term
  const filteredSubdivisions = subdivisionsData?.subdivisions?.filter(
    (subdivision) =>
      subdivision.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (subdivision.code && subdivision.code.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Create subdivision mutation
  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/subdivisions', data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('subdivisions-all');
        queryClient.invalidateQueries('subdivisions');
        toast.success(data.message || 'Subdivision created successfully!');
        setShowAddModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create subdivision');
      }
    }
  );

  // Update subdivision mutation
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/subdivisions/${id}`, data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('subdivisions-all');
        queryClient.invalidateQueries('subdivisions');
        toast.success(data.message || 'Subdivision updated successfully!');
        setShowEditModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update subdivision');
      }
    }
  );

  // Deactivate subdivision mutation
  const deactivateMutation = useMutation(
    async (id) => {
      const response = await api.put(`/subdivisions/${id}`, { isActive: false });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subdivisions-all');
        queryClient.invalidateQueries('subdivisions');
        toast.success('Subdivision deactivated successfully!');
        setShowDeactivateDialog(false);
        setSelectedSubdivision(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to deactivate subdivision');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      isActive: true,
      headUserId: ''
    });
    setSelectedSubdivision(null);
  };

  const handleEdit = (subdivision) => {
    setSelectedSubdivision(subdivision);
    setFormData({
      name: subdivision.name,
      code: subdivision.code || '',
      isActive: subdivision.isActive,
      headUserId: subdivision.headUserId || ''
    });
    setShowEditModal(true);
  };

  const handleDeactivate = (subdivision) => {
    setSelectedSubdivision(subdivision);
    setShowDeactivateDialog(true);
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate({
      name: formData.name,
      code: formData.code || undefined,
      headUserId: formData.headUserId || null
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate({
      id: selectedSubdivision.id,
      data: formData
    });
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="flex items-center text-green-600">
        <CheckCircle className="h-4 w-4 mr-1" />
        Active
      </span>
    ) : (
      <span className="flex items-center text-red-600">
        <XCircle className="h-4 w-4 mr-1" />
        Inactive
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Subdivision Management</h1>
          <p className="text-secondary-600 mt-1">Manage district subdivisions</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Subdivision
        </button>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search subdivisions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Subdivisions Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredSubdivisions.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-secondary-400" />
            <h3 className="mt-2 text-sm font-medium text-secondary-900">No subdivisions found</h3>
            <p className="mt-1 text-sm text-secondary-500">
              {searchTerm ? 'Try adjusting your search' : 'Get started by adding a subdivision'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Current Head
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredSubdivisions.map((subdivision) => (
                  <tr key={subdivision.id} className="hover:bg-secondary-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <MapPin className="h-5 w-5 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-secondary-900">
                            {subdivision.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-secondary-900">
                        {subdivision.code || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {subdivision.head ? (
                        <span className="text-sm text-secondary-900">
                          {subdivision.head.fullName}
                          <span className="ml-1 text-xs text-secondary-500">
                            ({subdivision.head.role.toUpperCase()})
                          </span>
                        </span>
                      ) : (
                        <span className="text-sm text-secondary-400">Not assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">{getStatusBadge(subdivision.isActive)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                      {formatDate(subdivision.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(subdivision)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit Subdivision"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {subdivision.isActive && (
                        <button
                          onClick={() => handleDeactivate(subdivision)}
                          className="text-red-600 hover:text-red-900"
                          title="Deactivate Subdivision"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Subdivision Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-secondary-900">Add New Subdivision</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-secondary-400 hover:text-secondary-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Mon"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Code
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., MON"
                    maxLength="10"
                  />
                  <p className="text-xs text-secondary-500 mt-1">
                    Short code for the subdivision (optional)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Current Head
                  </label>
                  <select
                    value={formData.headUserId}
                    onChange={(e) => setFormData({ ...formData, headUserId: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">-- Not Assigned --</option>
                    {eligibleHeadsData?.users?.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} ({user.role.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-secondary-500 mt-1">
                    Only ADC/DC users can be assigned as subdivision heads
                  </p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-secondary-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="btn btn-outline"
                  disabled={createMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Subdivision
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Subdivision Modal */}
      {showEditModal && selectedSubdivision && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-secondary-900">Edit Subdivision</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-secondary-400 hover:text-secondary-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Mon"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Code
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., MON"
                    maxLength="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Current Head
                  </label>
                  <select
                    value={formData.headUserId}
                    onChange={(e) => setFormData({ ...formData, headUserId: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">-- Not Assigned --</option>
                    {eligibleHeadsData?.users?.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} ({user.role.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-secondary-500 mt-1">
                    Only ADC/DC users can be assigned as subdivision heads
                  </p>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-secondary-900">
                    Active subdivision
                  </label>
                </div>
                {!formData.isActive && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800">
                      Deactivating a subdivision will hide it from permit creation and user assignment dropdowns.
                    </p>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-secondary-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="btn btn-outline"
                  disabled={updateMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Edit className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Dialog */}
      {showDeactivateDialog && selectedSubdivision && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">
                Deactivate Subdivision
              </h3>
              <p className="text-secondary-600 mb-4">
                Are you sure you want to deactivate <strong>{selectedSubdivision.name}</strong>?
                This will hide the subdivision from permit creation and user assignment dropdowns.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Existing permits and users associated with this subdivision will not be affected.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeactivateDialog(false);
                    setSelectedSubdivision(null);
                  }}
                  className="btn btn-outline"
                  disabled={deactivateMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => deactivateMutation.mutate(selectedSubdivision.id)}
                  className="btn bg-red-600 text-white hover:bg-red-700"
                  disabled={deactivateMutation.isLoading}
                >
                  {deactivateMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deactivating...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Deactivate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubdivisionManagement;
